import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { settingsApi, type NotificationChannelConfig, type NotificationTemplate } from "@/services/settings";
import { useCanAny } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { SettingsTabs } from "./SettingsTabs";

// 通道展示元数据（canvas·服务商/图标）· 发送统计=待接通知发送日志（v2）
const CH_META: Record<string, { icon: string; label: string; provider: string }> = {
  email: { icon: "📧", label: "邮件", provider: "AWS SES + SendGrid 兜底" },
  sms: { icon: "💬", label: "短信", provider: "Twilio（美国）+ 阿里云（中国）" },
  wechat: { icon: "🟢", label: "微信", provider: "企业微信 webhook + 公众号模板" },
  app: { icon: "📱", label: "App 推送", provider: "Firebase Cloud Messaging（v2 阶段）" },
};
const CH_CHIP: Record<string, string> = { email: "📧 邮件", sms: "💬 短信", wechat: "🟢 微信", app: "📱 App" };
const AUD = [
  { key: "all", label: "全部" },
  { key: "customer", label: "给客户" },
  { key: "staff", label: "给员工" },
  { key: "owner", label: "给老板" },
] as const;

export function NotificationsSettingsPage() {
  const qc = useQueryClient();
  const canEdit = useCanAny(["notifications.config.manage", "notifications.templates.edit"]);
  const [tab, setTab] = useState<string>("all");

  const ch = useQuery({ queryKey: ["settings", "channels"], queryFn: settingsApi.getChannels });
  const tpl = useQuery({ queryKey: ["settings", "templates"], queryFn: settingsApi.getTemplates });

  const toggleChannel = useMutation({
    mutationFn: (c: NotificationChannelConfig) => settingsApi.patchChannels([{ ...c, enabled: !c.enabled }]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "channels"] }),
    onError: () => alert("通道开关保存失败，请重试"),
  });
  const toggleTpl = useMutation({
    mutationFn: (t: NotificationTemplate) => settingsApi.patchTemplates([{ ...t, enabled: !t.enabled }]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "templates"] }),
    onError: () => alert("模板开关保存失败，请重试"),
  });

  const templates = tpl.data ?? [];
  const counts = useMemo(() => ({
    all: templates.length,
    customer: templates.filter((t) => t.audience === "customer").length,
    staff: templates.filter((t) => t.audience === "staff").length,
    owner: templates.filter((t) => t.audience === "owner").length,
  }), [templates]);
  const shown = tab === "all" ? templates : templates.filter((t) => t.audience === tab);

  if (ch.isLoading || tpl.isLoading) return <LoadingState />;
  if (ch.isError || tpl.isError)
    return <ErrorState message="通知配置加载失败" onRetry={() => { ch.refetch(); tpl.refetch(); }} />;
  const channels = (["email", "sms", "wechat", "app"] as const).map(
    (c) => (ch.data ?? []).find((x) => x.channel === c) ?? { channel: c, enabled: false, sender: null, api_key_ref: null },
  );

  return (
    <PageShell title="系统设置 · 通知配置" subtitle="4 通道（邮件/短信/微信/App）+ 模板（按事件类型）· 阈值已分散到各业务模块">
      <SettingsTabs />
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
        <Lock size={14} /> 权限：老板 + 技术管理员配置通道 · 各业务负责人编辑自己模块的模板
      </div>

      {/* 📡 通知通道 */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-base font-semibold">📡 通知通道（4 类）</h3>
        <p className="mb-4 text-[12px] text-slate-500">配 API key / 服务商 / 测试 · 按通道分（邮件富文本 / 短信纯文本 / 微信富文本）</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {channels.map((c) => {
            const m = CH_META[c.channel];
            return (
              <div key={c.channel} className={`rounded-xl border-2 p-4 ${c.enabled ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2"><span className="text-2xl">{m.icon}</span><div><div className="text-[14px] font-semibold">{m.label}</div><div className="text-[11px] text-slate-500">服务商：{m.provider}</div></div></div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.enabled ? "● 已启用" : "未启用"}</span>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div><div className="text-[13px] text-slate-400">发送统计待接 ⏳</div><div className="text-[11px] text-slate-400">通知发送日志 v2</div></div>
                  <div className="flex gap-2">
                    <button disabled title="测试发送待接（需真实通道接入）" className="rounded-lg border border-slate-200 px-2.5 py-1 text-[12px] text-slate-300">测试发送 ⏳</button>
                    <button disabled={!canEdit || toggleChannel.isPending} onClick={() => toggleChannel.mutate(c)} className="rounded-lg bg-blue-600 px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-50">{c.enabled ? "关闭" : "启用"}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 📝 通知模板 */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">📝 通知模板（按事件类型）</h3>
          {canEdit && <button disabled title="新建模板待接（模板编辑器 v2）" className="h-8 rounded-lg border border-slate-200 px-3 text-[12px] font-medium text-slate-300">+ 新模板 ⏳</button>}
        </div>
        <p className="mt-0.5 text-[12px] text-slate-500">每模板按通道分版本（邮件富文本 / 短信简版 / 微信卡片）</p>
        <div className="mt-3 flex gap-2">
          {AUD.map((a) => (
            <button key={a.key} onClick={() => setTab(a.key)} className={`rounded-lg px-3 py-1.5 text-[12px] font-medium ${tab === a.key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>
              {a.label} {counts[a.key as keyof typeof counts]}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-col gap-2">
          {shown.map((t) => (
            <div key={t.code} className="flex items-center gap-4 rounded-xl border border-slate-200 p-3.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-900">{t.icon} {t.label_cn}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{t.channels.map((c) => CH_CHIP[c] ?? c).join(" + ")}</div>
                <div className="mt-1 truncate text-[12px] text-slate-600">{t.subject}</div>
              </div>
              <div className="shrink-0 text-right text-[11px] text-slate-400">{t.updated_at}<div>上次编辑</div></div>
              <button disabled={!canEdit || toggleTpl.isPending} onClick={() => toggleTpl.mutate(t)} className={`shrink-0 rounded-lg px-2.5 py-1 text-[12px] font-medium ${t.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"} disabled:opacity-50`}>{t.enabled ? "● 启用" : "停用"}</button>
              <button disabled title="模板内容编辑待接（模板编辑器 v2）" className="shrink-0 rounded-lg border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-300">编辑 ⏳</button>
            </div>
          ))}
        </div>
      </section>

      {/* 🌙 静默时段配置 */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-base font-semibold">🌙 静默时段配置</h3>
        <p className="mb-4 text-[12px] text-slate-500">避免凌晨打扰客户 · 业务通知按通道差异</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <QuietCard icon="📧" title="邮件" lines={["24 小时随时发", "用户邮箱自由控制"]} />
          <QuietCard icon="💬" title="短信" lines={["22:00 - 08:00 静默", "紧急派单除外（< 12h 出团）", "时区按客户所在地"]} />
          <QuietCard icon="🟢" title="微信" lines={["22:00 - 09:00 静默", "公告类不静默"]} />
        </div>
      </section>
    </PageShell>
  );
}

function QuietCard({ icon, title, lines }: { icon: string; title: string; lines: string[] }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="text-[13px] font-semibold">{icon} {title}</div>
      <ul className="mt-1.5 flex flex-col gap-0.5 text-[12px] text-slate-500">
        {lines.map((l, i) => <li key={i}>· {l}</li>)}
      </ul>
    </div>
  );
}
